<app-header-latest></app-header-latest>
<!-- HERO -->
<div class="start-hero">
  <div class="container-wrapper">
    <div>
      <div class="section-eyebrow">🚀 STARTUP SCALE</div>
      <h1>Move Fast,<br>Stay <span style="color:#C084FC;">Lean &amp; Compliant</span></h1>
      <p>The payroll, directory, and hiring software built for fast-moving startups. Get fully set up in under 30 minutes, with zero IT overhead.</p>
      <div style="display:flex;gap:16px;flex-wrap:wrap;">
        <a href="#" class="btn btn-white">Get Started Free</a>
        <a href="#" class="btn btn-outline" style="color:#fff;border-color:rgba(255,255,255,0.3);">Watch Onboarding Walkthrough</a>
      </div>
    </div>
    <div>
      <div class="scale-widget">
        <div class="scale-widget-title">Startup Growth Simulation</div>
        <div class="scale-slider">
          <div class="scale-track"><div class="scale-fill"></div><div class="scale-thumb"></div></div>
        </div>
        <div class="scale-stats">
          <div><div class="scale-stat-num">45</div><div class="scale-stat-lbl">Employees</div></div>
          <div><div class="scale-stat-num">$180</div><div class="scale-stat-lbl">Monthly Cost</div></div>
          <div><div class="scale-stat-num">100%</div><div class="scale-stat-lbl">Automated</div></div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- SETUP STEPS -->
<section class="setup-steps-section">
  <div class="container-wrapper">
    <div class="sec-head">
      <span class="section-eyebrow">⚡ Instant Implementation</span>
      <h2 class="sec-h2">Setup in Under<br><span class="gradient-text">30 Minutes.</span></h2>
      <p class="sec-sub">Skip weeks of HR implementation calls. We make startup deployment seamless from day one.</p>
    </div>
    <div class="setup-grid">
      <div class="setup-card">
        <div class="setup-num">01</div>
        <div class="setup-card-title">Excel Import</div>
        <div class="setup-card-desc">Drag and drop your previous employee record spreadsheet. Our parser maps profiles instantly, no reformatting needed.</div>
      </div>
      <div class="setup-card">
        <div class="setup-num">02</div>
        <div class="setup-card-title">Sync Google Workspace</div>
        <div class="setup-card-desc">Connect Google Workspace or MS365 with one click. Sync company rosters and map calendars automatically to NeoHRMZ.</div>
      </div>
      <div class="setup-card">
        <div class="setup-num">03</div>
        <div class="setup-card-title">Run First Payroll</div>
        <div class="setup-card-desc">Enter your bank routing details, lock hours, and let NeoHRMZ calculate and disburse salaries to every employee automatically.</div>
      </div>
    </div>
  </div>
</section>

<!-- PRICING SNAPSHOT -->
<section class="startup-pricing">
  <div class="container-wrapper">
    <div class="sec-head">
      <span class="section-eyebrow">💜 Startup-Friendly Pricing</span>
      <h2 class="sec-h2">Transparent Plans<br><span class="gradient-text">That Scale With You</span></h2>
      <p class="sec-sub">No hidden fees. No enterprise contracts. Pay per seat, scale up anytime — no surprise bills.</p>
    </div>
    <div class="startup-price-grid">
      <div class="startup-price-card">
        <div class="startup-price-card__tier">Seed</div>
        <div class="startup-price-card__price"><sup>$</sup>4</div>
        <div class="startup-price-card__period">per employee / month</div>
        <div class="startup-price-card__features">
          <div class="startup-price-card__feat">Up to 25 employees</div>
          <div class="startup-price-card__feat">Core payroll &amp; attendance</div>
          <div class="startup-price-card__feat">Employee self-service portal</div>
          <div class="startup-price-card__feat">Slack + GSuite integration</div>
        </div>
        <a href="#" class="btn btn-outline" style="width:100%;justify-content:center;">Start Free Trial</a>
      </div>
      <div class="startup-price-card startup-price-card--featured">
        <div class="startup-price-card__tier">Growth</div>
        <div class="startup-price-card__price"><sup>$</sup>8</div>
        <div class="startup-price-card__period">per employee / month</div>
        <div class="startup-price-card__features">
          <div class="startup-price-card__feat">Up to 150 employees</div>
          <div class="startup-price-card__feat">Payroll + performance OKRs</div>
          <div class="startup-price-card__feat">Recruitment pipeline (ATS)</div>
          <div class="startup-price-card__feat">AI assistant &amp; analytics</div>
        </div>
        <a href="#" class="btn btn-primary" style="width:100%;justify-content:center;">Get Started</a>
      </div>
      <div class="startup-price-card">
        <div class="startup-price-card__tier">Scale</div>
        <div class="startup-price-card__price"><sup>$</sup>14</div>
        <div class="startup-price-card__period">per employee / month</div>
        <div class="startup-price-card__features">
          <div class="startup-price-card__feat">Unlimited employees</div>
          <div class="startup-price-card__feat">Multi-entity &amp; global payroll</div>
          <div class="startup-price-card__feat">Advanced audit &amp; compliance</div>
          <div class="startup-price-card__feat">Dedicated success manager</div>
        </div>
        <a href="#" class="btn btn-outline" style="width:100%;justify-content:center;">Talk to Sales</a>
      </div>
    </div>
  </div>
</section>

<!-- INTEGRATIONS -->
<section class="startup-integrations">
  <div class="container-wrapper">
    <div class="sec-head">
      <span class="section-eyebrow">🔌 Plug &amp; Play</span>
      <h2 class="sec-h2">Works with the<br><span class="gradient-text">Tools You Already Use</span></h2>
      <p class="sec-sub">NeoHRMZ connects to your existing startup stack in minutes — no API expertise required.</p>
    </div>
    <div class="startup-int-grid">
      <div class="startup-int-badge"><div class="startup-int-badge__icon">📧</div><div class="startup-int-badge__name">Gmail</div></div>
      <div class="startup-int-badge"><div class="startup-int-badge__icon">💬</div><div class="startup-int-badge__name">Slack</div></div>
      <div class="startup-int-badge"><div class="startup-int-badge__icon">📁</div><div class="startup-int-badge__name">Google Drive</div></div>
      <div class="startup-int-badge"><div class="startup-int-badge__icon">🎯</div><div class="startup-int-badge__name">Jira</div></div>
      <div class="startup-int-badge"><div class="startup-int-badge__icon">🏦</div><div class="startup-int-badge__name">Stripe</div></div>
      <div class="startup-int-badge"><div class="startup-int-badge__icon">🔗</div><div class="startup-int-badge__name">Notion</div></div>
      <div class="startup-int-badge"><div class="startup-int-badge__icon">📊</div><div class="startup-int-badge__name">QuickBooks</div></div>
      <div class="startup-int-badge"><div class="startup-int-badge__icon">🪄</div><div class="startup-int-badge__name">Zapier</div></div>
      <div class="startup-int-badge"><div class="startup-int-badge__icon">🐙</div><div class="startup-int-badge__name">GitHub</div></div>
      <div class="startup-int-badge"><div class="startup-int-badge__icon">📅</div><div class="startup-int-badge__name">Calendar</div></div>
      <div class="startup-int-badge"><div class="startup-int-badge__icon">☁️</div><div class="startup-int-badge__name">AWS</div></div>
      <div class="startup-int-badge"><div class="startup-int-badge__icon">🔍</div><div class="startup-int-badge__name">Greenhouse</div></div>
    </div>
  </div>
</section>

<!-- CTA -->
<section class="startup-cta">
  <div class="container-wrapper">
    <h2 class="startup-cta__h2">Launch Your Startup's HR<br>Stack Today</h2>
    <p class="startup-cta__p">Join 1,400+ startups who run smarter HR operations with NeoHRMZ. Free for your first 30 days.</p>
    <div class="startup-cta__actions">
      <a href="#" class="btn btn-white">Start for Free</a>
      <a href="#" class="btn btn-outline" style="color:#fff;border-color:rgba(255,255,255,0.3);">Book a Demo</a>
    </div>
  </div>
</section>
<app-peopleflow-footer></app-peopleflow-footer>